// Setting.tsx
import React, { useEffect, useState, useCallback } from "react";
import {
  Upload,
  message,
  Row,
  Col,
  Card,
  Form,
  Modal,
  Button,
  Input,
  Image,
  Tabs,
  Select,
  Table,
  Tag,
  Tooltip,
  Space,
  Spin,
  Empty,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { InfoCircleOutlined, SearchOutlined } from "@ant-design/icons";
import { FaPen, FaTrashAlt } from "react-icons/fa";
import { RiAddBoxFill, RiCheckLine, RiFileCopyLine } from "react-icons/ri";
import {
  RcFile,
  UploadChangeParam,
  UploadFile,
} from "antd/lib/upload/interface";
import { useNavigate } from "react-router-dom";
import { BiUpload } from "react-icons/bi";
import {
  addSetting,
  getsetting,
  updateSetting,
  getAiSettings,
  createAiSetting,
  updateAiSetting,
  deleteAiSetting,
} from "../../utils/API";
import { useSettings } from "./SettingContext";
import TabPane from "antd/es/tabs/TabPane";

interface AiConfig {
  _id: string;
  title: string;
  apiKey: string;
  model: string;
  provider: string;
  apiBase?: string;
  systemPrompt?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

const Setting: React.FC = () => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [logo, setLogo] = useState<UploadFile[]>([]);
  const [favicon, setfavicon] = useState<UploadFile[]>([]);
  const [previewVisible, setPreviewVisible] = useState<boolean>(false);
  const [previewImage, setPreviewImage] = useState<string>("");
  const [logopreview, setlogopreview] = useState<string>("");
  const imageLink = process.env.REACT_APP_IMAGE_URL;
  const [faviconpreview, setfaviconpreview] = useState<string>("");
  const { settings: contextSettings, updateSettings } = useSettings();
  const [aiModelSearchValue, setAiModelSearchValue] = useState("");
  
  const [form] = Form.useForm();
  const currentModelValue = Form.useWatch("aiModel", form);
  const navigate = useNavigate();

  // AI settings states
  const [aiSettings, setAiSettings] = useState<AiConfig[]>([]);
  const [aiLoading, setAiLoading] = useState<boolean>(false);
  const [aiPage, setAiPage] = useState<number>(1);
  const [aiTotal, setAiTotal] = useState<number>(0);
  const [aiSearchText, setAiSearchText] = useState<string>("");
  const [aiSearchInput, setAiSearchInput] = useState<string>("");
  const [activeSettingsTab, setActiveSettingsTab] = useState<string>("1");
  const [copiedAiKeyId, setCopiedAiKeyId] = useState<string | null>(null);
  const [aiModalVisible, setAiModalVisible] = useState<boolean>(false);
  const [editingAi, setEditingAi] = useState<AiConfig | null>(null);
  const [aiDeleteId, setAiDeleteId] = useState<string | null>(null);
  const [aiForm] = Form.useForm();
  const currentAiModelValue = Form.useWatch("model", aiForm);
  const PAGE_SIZE = 10;

  // Global system prompt (applies to all models)
  const [globalPrompt, setGlobalPrompt] = useState<string>("");
  const [globalPromptSaving, setGlobalPromptSaving] = useState<boolean>(false);

  const fetchAiSettings = useCallback(async (currentPage: number, search: string) => {
    try {
      setAiLoading(true);
      const response = await getAiSettings({ page: currentPage, limit: PAGE_SIZE, search });
      if (response.success) {
        setAiSettings(response.data || []);
        setAiTotal(response.total || 0);
      } else {
        message.error(response.message || "Failed to fetch AI configurations");
      }
    } catch (error) {
      console.error("Error fetching AI settings:", error);
      message.error("Failed to fetch AI configurations");
    } finally {
      setAiLoading(false);
    }
  }, []);

  // Fetch AI settings when page or search text changes
  useEffect(() => {
    fetchAiSettings(aiPage, aiSearchText);
  }, [aiPage, aiSearchText, fetchAiSettings]);

  // Debounced search for AI settings
  useEffect(() => {
    const timer = setTimeout(() => {
      setAiSearchText(aiSearchInput);
      setAiPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [aiSearchInput]);

  const handleAddAi = () => {
    setEditingAi(null);
    aiForm.resetFields();
    setAiModalVisible(true);
  };

  const copyToClipboard = (text: string) => {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text);
    } else {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    }
  };

  const handleCopyAiKey = (rowId: string, apiKey: string) => {
    if (!apiKey) return;
    copyToClipboard(apiKey);
    setCopiedAiKeyId(rowId);

    setTimeout(() => {
      setCopiedAiKeyId((currentId) => (currentId === rowId ? null : currentId));
    }, 1500);
  };

  const handleEditAi = (config: AiConfig) => {
    setEditingAi(config);
    aiForm.setFieldsValue({
      title: config.title,
      apiKey: config.apiKey,
      model: config.model,
      provider: config.provider,
      apiBase: config.apiBase,
      systemPrompt: config.systemPrompt,
      isActive: config.isActive,
    });
    setAiModalVisible(true);
  };

  const handleDeleteAiConfirmation = (id: string) => setAiDeleteId(id);

  const handleDeleteAi = async () => {
    if (!aiDeleteId) return;
    try {
      setAiLoading(true);
      const response = await deleteAiSetting(aiDeleteId);
      if (response.success) {
        message.success("AI Configuration deleted successfully!");
        const newPage = aiSettings.length === 1 && aiPage > 1 ? aiPage - 1 : aiPage;
        setAiPage(newPage);
        fetchAiSettings(newPage, aiSearchText);
      } else {
        message.error(response.message || "Failed to delete AI Configuration");
      }
      setAiDeleteId(null);
    } catch (error) {
      console.error("Error deleting AI Configuration:", error);
      message.error("Failed to delete AI Configuration");
    } finally {
      setAiLoading(false);
    }
  };

  const handleToggleAiStatus = async (id: string, currentStatus: boolean) => {
    try {
      const newStatus = !currentStatus;
      const response = await updateAiSetting(id, { isActive: newStatus });
      if (response.success) {
        message.success(`AI Configuration ${newStatus ? "Activated" : "Deactivated"} successfully!`);
        fetchAiSettings(aiPage, aiSearchText);
      } else {
        message.error(response.message || "Failed to update status");
      }
    } catch (error) {
      console.error("Error updating AI Configuration status:", error);
      message.error("Failed to update status");
    }
  };

  const handleAiSubmit = async () => {
    try {
      const values = await aiForm.validateFields();
      setAiLoading(true);
      let response;
      if (editingAi) {
        response = await updateAiSetting(editingAi._id, values);
      } else {
        response = await createAiSetting(values);
      }

      if (response.success) {
        message.success(editingAi ? "AI Configuration updated successfully!" : "AI Configuration added successfully!");
        setAiModalVisible(false);
        aiForm.resetFields();
        fetchAiSettings(aiPage, aiSearchText);
      } else {
        message.error(response.message || "Failed to save AI Configuration");
      }
    } catch (error) {
      console.error("Error saving AI Configuration:", error);
      message.error("Failed to save AI Configuration");
    } finally {
      setAiLoading(false);
    }
  };

  const handleLogoImage = (info: UploadChangeParam<UploadFile<any>>) => {
    const { file } = info;
    // Display preview for the uploaded file
    if (file.originFileObj) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setlogopreview(e.target?.result as string); // Set preview URL
      };
      reader.readAsDataURL(file.originFileObj);
    } else {
      setlogopreview(null as any); // Clear preview if no file is selected or upload is in progress
    }
    setLogo(info.fileList); // Update fileList state
  };

  const handlefaviconImage = (info: UploadChangeParam<UploadFile<any>>) => {
    const { file } = info;
    if (file.originFileObj) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setfaviconpreview(e.target?.result as string); // Set preview URL
      };
      reader.readAsDataURL(file.originFileObj);
    } else {
      setfaviconpreview(null as any); // Clear preview if no file is selected or upload is in progress
    }
    setfavicon(info.fileList); // Update fileList state
  };

  useEffect(() => {
    getsetting()
      .then((response) => {
        const settingData = response.settings[0];
        form.setFieldsValue(settingData);

        const logoImage = `${imageLink}/settings/${settingData.logo}`;
        const faviconImage = `${imageLink}/settings/${settingData.favicon}`;

        setlogopreview(logoImage);
        setfaviconpreview(faviconImage);
        setLogo([{ uid: "-1", name: "", status: "done", url: logoImage }]);
        setfavicon([
          { uid: "-1", name: "favicon", status: "done", url: faviconImage },
        ]);

        // Load global system prompt
        if (settingData.aiSystemPrompt) {
          setGlobalPrompt(settingData.aiSystemPrompt);
        }

        // Update context with the fetched settings
        updateSettings({
          siteName: settingData.siteName,
          logo: logoImage,
          favicon: faviconImage
        });
      })
      .catch((error) => {
        console.error("Error fetching setting data:", error);
      });
  }, []);

  // Save global system prompt
  const handleSaveGlobalPrompt = async () => {
    try {
      setGlobalPromptSaving(true);
      const id = form.getFieldValue("_id");
      const formData = new FormData();
      formData.append("aiSystemPrompt", globalPrompt);
      await updateSetting(id, formData);
      message.success("Global system prompt saved successfully!");
    } catch (error) {
      console.error("Error saving global system prompt:", error);
      message.error("Failed to save system prompt");
    } finally {
      setGlobalPromptSaving(false);
    }
  };


  const handleSubmit = async () => {
    const id = form.getFieldValue("_id");
    try {
      const formData = new FormData();
      formData.append("siteName", form.getFieldValue("siteName"));
      formData.append("phone", form.getFieldValue("phone"));
      formData.append("address", form.getFieldValue("address"));
      formData.append("aboutus", form.getFieldValue("aboutus"));
      formData.append("contactEmail", form.getFieldValue("contactEmail"));
      formData.append("facebookLink", form.getFieldValue("facebookLink"));
      formData.append("linkedinLink", form.getFieldValue("linkedinLink"));
      formData.append("instagramLink", form.getFieldValue("instagramLink"));
      formData.append("twitterLink", form.getFieldValue("twitterLink"));
      formData.append("copyrightYear", form.getFieldValue("copyrightYear"));
      formData.append("adminEmail", form.getFieldValue("adminEmail"));
      formData.append("thirdPartyAppToken", form.getFieldValue("thirdPartyAppToken"));
      formData.append("smtpHost", form.getFieldValue("smtpHost"));
      formData.append("smtpPort", form.getFieldValue("smtpPort"));
      formData.append("smtpUsername", form.getFieldValue("smtpUsername"));
      formData.append("smtpPassword", form.getFieldValue("smtpPassword"));
      formData.append("encryption", form.getFieldValue("encryption"));
      formData.append("fromEmail", form.getFieldValue("fromEmail"));
      formData.append("fromName", form.getFieldValue("fromName"));
      formData.append("aiApiKey", form.getFieldValue("aiApiKey"));
      formData.append("aiSystemPrompt", form.getFieldValue("aiSystemPrompt"));
      formData.append("aiModel", form.getFieldValue("aiModel") || "gemini-1.5-flash");
      formData.append("aiProvider", form.getFieldValue("aiProvider") || "google");
      formData.append("aiApiBase", form.getFieldValue("aiApiBase") || "");

      // Append logo if it exists
      if (logo.length > 0 && logo[0].originFileObj) {
        formData.append("logo", logo[0].originFileObj as Blob);
      }

      // Append favicon if it exists
      if (favicon.length > 0 && favicon[0].originFileObj) {
        formData.append("favicon", favicon[0].originFileObj as Blob);
      }
      let response;

      if (id) {
        // If `id` exists, update the setting
        response = await updateSetting(id, formData);
      } else {
        // If no `id`, add a new setting
        response = await addSetting(formData);
      }
      const updatedSettings = await getsetting();
      const settingData = updatedSettings.settings[0];
      const logoImage = `${imageLink}/settings/${settingData.logo}`;
      const faviconImage = `${imageLink}/settings/${settingData.favicon}`;

      // Update context with new settings
      updateSettings({
        siteName: settingData.siteName,
        logo: logoImage,
        favicon: faviconImage
      });

      message.success(response.data.message);
      // getsetting();
      navigate("/settings");
    } catch (error) {
      console.error("Update failed:", error);
    }
  };

  const defaultModelOptions = [
    { label: "gemini-2.5-flash", value: "gemini-2.5-flash" },
    { label: "gemini-2.5-flash-lite", value: "gemini-2.5-flash-lite" },
    { label: "openrouter/free (Auto Free Router)", value: "openrouter/free" },
    { label: "openai/gpt-oss-20b:free", value: "openai/gpt-oss-20b:free" },
    { label: "meta-llama/llama-3.3-70b-instruct:free", value: "meta-llama/llama-3.3-70b-instruct:free" },
    { label: "meta-llama/llama-3.2-3b-instruct:free", value: "meta-llama/llama-3.2-3b-instruct:free" },
    { label: "deepseek/deepseek-v4-flash:free", value: "deepseek/deepseek-v4-flash:free" },
    { label: "google/gemma-4-31b-it:free", value: "google/gemma-4-31b-it:free" },
  ];

  const computedOptions = [...defaultModelOptions];
  if (aiModelSearchValue && !defaultModelOptions.some(opt => opt.value === aiModelSearchValue)) {
    computedOptions.unshift({ label: aiModelSearchValue, value: aiModelSearchValue });
  }
  if (currentModelValue && !computedOptions.some(opt => opt.value === currentModelValue)) {
    computedOptions.unshift({ label: currentModelValue, value: currentModelValue });
  }

  const computedAiOptions = [...defaultModelOptions];
  if (aiModelSearchValue && !defaultModelOptions.some(opt => opt.value === aiModelSearchValue)) {
    computedAiOptions.unshift({ label: aiModelSearchValue, value: aiModelSearchValue });
  }
  if (currentAiModelValue && !computedAiOptions.some(opt => opt.value === currentAiModelValue)) {
    computedAiOptions.unshift({ label: currentAiModelValue, value: currentAiModelValue });
  }

  const aiColumns: ColumnsType<AiConfig> = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (v: string) => <Tag color="purple" style={{ fontWeight: 500 }}>{v}</Tag>,
    },
    {
      title: "API Key",
      dataIndex: "apiKey",
      key: "apiKey",
      render: (v: string, record) => {
        if (!v) return "";
        const maskedKey = v.length > 8 ? `${v.substring(0, 4)}••••${v.substring(v.length - 4)}` : "••••••••";
        const isCopied = copiedAiKeyId === record._id;

        return (
          <div
            className="lead-source-wrapper"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <span>{maskedKey}</span>
            <Tooltip
              title={isCopied ? "Copied" : "Copy API Key"}
              color="#CCCCCC"
              overlayInnerStyle={{ color: "#000", padding: "12px" }}
            >
              {isCopied ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    lineHeight: 1,
                  }}
                >
                  <RiCheckLine size={20} style={{ color: "#1890ff" }} />
                  <span
                    style={{
                      fontSize: "9px",
                      color: "#1890ff",
                      marginTop: "2px",
                    }}
                  >
                    Copied
                  </span>
                </div>
              ) : (
                <RiFileCopyLine
                  size={20}
                  className="copy-icon"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCopyAiKey(record._id, v);
                  }}
                />
              )}
            </Tooltip>
          </div>
        );
      }
    },
    {
      title: "Model",
      dataIndex: "model",
      key: "model",
      render: (v: string) => <Tag color="blue">{v}</Tag>,
    },
    {
      title: "Provider",
      dataIndex: "provider",
      key: "provider",
      render: (v: string) => <span style={{ textTransform: "capitalize", fontWeight: 500 }}>{v}</span>,
    },
    {
      title: "API Base Endpoint",
      dataIndex: "apiBase",
      key: "apiBase",
      render: (v: string) => <span style={{ color: "#666" }}>{v || "Default"}</span>,
    },
    {
      title: "Status",
      dataIndex: "isActive",
      key: "isActive",
      align: "center" as const,
      width: 100,
      render: (isActive: boolean, record: AiConfig) => (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
          }}
        >
          <label
            style={{
              position: "relative",
              display: "inline-block",
              width: 40,
              height: 22,
              backgroundColor: isActive ? "#001529" : "#f0f0f0",
              borderRadius: 13,
              cursor: "pointer",
              transition: "background-color 0.3s ease",
              boxShadow: isActive
                ? "inset 0 0 0 2px #001529"
                : "inset 0 0 0 1px #ccc",
            }}
          >
            <input
              type="checkbox"
              checked={isActive}
              onChange={() => handleToggleAiStatus(record._id, isActive)}
              style={{ display: "none" }}
            />
            <span
              style={{
                position: "absolute",
                width: 21,
                height: 19,
                left: isActive ? "calc(99% - 21px)" : 2,
                top: 1,
                backgroundColor: "#ffffff",
                borderRadius: "50%",
                boxShadow: "0 2px 5px rgba(0, 0, 0, 0.2)",
                transition: "left 0.3s ease, box-shadow 0.3s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 10,
                fontWeight: "bold",
                color: isActive ? "#001529" : "#666",
              }}
            >
              {isActive ? "ON" : "OFF"}
            </span>
          </label>
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center" as const,
      width: 120,
      render: (_, record) => (
        <div className="d-flex" style={{ gap: "8px", alignItems: "center", justifyContent: "center", width: "100%" }}>
          <Tooltip title="Edit">
            <button
              onClick={() => handleEditAi(record)}
              className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 shadow-[0_4px_12px_rgba(139,92,246,0.3)] hover:shadow-[0_6px_16px_rgba(139,92,246,0.4)] active:translate-y-[1px] active:shadow-none text-white flex items-center justify-center transition-all duration-200 ease-in-out group"
            >
              <FaPen className="group-hover:scale-110 transition-transform duration-200" size={12} />
            </button>
          </Tooltip>
          <Tooltip title="Delete">
            <button
              onClick={() => handleDeleteAiConfirmation(record._id)}
              className="w-7 h-7 rounded-full bg-gradient-to-br from-rose-500 to-rose-700 shadow-[0_4px_12px_rgba(244,63,94,0.3)] hover:shadow-[0_6px_16px_rgba(244,63,94,0.4)] active:translate-y-1 active:shadow-none text-white flex items-center justify-center transition-all duration-200 group"
            >
              <FaTrashAlt className="group-hover:scale-110 transition-transform" size={12} />
            </button>
          </Tooltip>
        </div>
      ),
    },
  ];

  return (
    <div className="p-2 from-[#f0f4ff] to-[#e2eafc] min-h-screen">
      <Row gutter={[16, 16]} justify="center">
        <Col xs={24} sm={24} md={24}>
          <Card
            className="rounded-2xl border border-gray-100 backdrop-blur-md bg-white/70"
            bodyStyle={{ padding: 0 }}
          >
            <div className="p-6">
              <Tabs
                activeKey={activeSettingsTab}
                onChange={setActiveSettingsTab}
                tabBarGutter={32}
                className="text-left"
                tabPosition="top"
                tabBarExtraContent={
                  activeSettingsTab === "4" ? (
                    <Button type="primary" onClick={handleAddAi} className="allbuttons">
                      <RiAddBoxFill className="button-icon" />
                      <span className="button-text">Add AI Configuration</span>
                    </Button>
                  ) : null
                }
              >
                <TabPane tab="General" key="1">
                  <Form className="bg-white" onFinish={handleSubmit} form={form}>
                    <Row
                      className="border border-warning-2 p-5 bg-white rounded-md"
                      style={{ marginLeft: 0, marginRight: 0 }}
                    >
                      <Col xs={24} sm={24} md={24} xl={24} xxl={24}>
                        <Row className="bg-white">
                          <Col
                            span={24}
                            className="d-flex justify-content-start align-items-center"
                          ></Col>
                          <Col span={24}>
                            <Row>
                              <Col
                                xs={24} sm={24} md={4}
                                className="d-flex justify-content-start me-4 bg-white lg:mb-5"
                              >
                                <label className="font-bold">
                                  Site Name <span className="text-danger">*</span>
                                </label>
                              </Col>
                              <Col xs={24} sm={24} md={12}>
                                <Form.Item
                                  name="siteName"
                                  rules={[
                                    {
                                      required: true,
                                      message: "Please Enter Site name",
                                    },
                                  ]}
                                >
                                  <Input
                                    size="large"
                                    className="rounded border border-1"
                                    placeholder="Site Name"
                                  />
                                </Form.Item>
                              </Col>
                            </Row>
                          </Col>

                          <Col span={24}>
                            <Row>
                              <Col
                                xs={24} sm={24} md={4}
                                className="d-flex justify-content-start me-4 bg-white lg:mb-5"
                              >
                                <label className="font-bold">
                                  Phone <span className="text-danger">*</span>
                                </label>
                              </Col>
                              <Col xs={24} sm={24} md={12}>
                                <Form.Item
                                  name="phone"
                                  rules={[
                                    {
                                      required: true,
                                      message: "Please Enter Phone",
                                    },
                                  ]}
                                >
                                  <Input
                                    type="number"
                                    size="large"
                                    className="rounded border border-1"
                                    placeholder="Phone"
                                  />
                                </Form.Item>
                              </Col>
                            </Row>
                          </Col>

                          <Col span={24}>
                            <Row>
                              <Col
                                xs={24} sm={24} md={4}
                                className="d-flex justify-content-start me-4 bg-white lg:mb-5"
                              >
                                <label className="font-bold">
                                  Address<span className="text-danger">*</span>
                                </label>
                              </Col>
                              <Col xs={24} sm={24} md={12}>
                                <Form.Item
                                  name="address"
                                  rules={[
                                    {
                                      required: true,
                                      message: "Please Enter Site Address",
                                    },
                                  ]}
                                >
                                  <Input.TextArea
                                    autoSize={{ minRows: 3, maxRows: 5 }}
                                    className="rounded border border-1"
                                    placeholder="Address"
                                  />
                                </Form.Item>
                              </Col>
                            </Row>
                          </Col>

                          <Col span={24}>
                            <Row>
                              <Col
                                xs={24} sm={24} md={4}
                                className="d-flex justify-content-start me-4 bg-white lg:mb-5"
                              >
                                <label className="font-bold">
                                  About Us <span className="text-danger">*</span>
                                </label>
                              </Col>
                              <Col xs={24} sm={24} md={12}>
                                <Form.Item
                                  name="aboutus"
                                  rules={[
                                    {
                                      required: true,
                                      message: "Please Tell some about us",
                                    },
                                  ]}
                                >
                                  <Input.TextArea
                                    autoSize={{ minRows: 3, maxRows: 5 }}
                                    className="rounded border border-1"
                                    placeholder="About Us"
                                  />
                                </Form.Item>
                              </Col>
                            </Row>
                          </Col>

                          <Col span={24}>
                            <Row>
                              <Col
                                xs={24} sm={24} md={4}
                                className="d-flex justify-content-start me-4 bg-white lg:mb-5"
                              >
                                <label className="font-bold">
                                  Copyright Year <span className="text-danger">*</span>
                                </label>
                              </Col>
                              <Col xs={24} sm={24} md={12}>
                                <Form.Item
                                  name="copyrightYear"
                                  rules={[
                                    {
                                      required: true,
                                      message: "Please Enter Copyright Year",
                                    },
                                  ]}
                                >
                                  <Input
                                    size="large"
                                    className="rounded border border-1"
                                    placeholder="Copyright Year"
                                  />
                                </Form.Item>
                              </Col>
                            </Row>
                          </Col>

                          <Col span={24}>
                            <Row>
                              <Col
                                xs={24} sm={24} md={4}
                                className="d-flex justify-content-start me-4 bg-white lg:mb-5"
                              >
                                <label className="font-bold">
                                  Admin E-mail <span className="text-danger">*</span>
                                </label>
                              </Col>
                              <Col xs={24} sm={24} md={12}>
                                <Form.Item
                                  name="adminEmail"
                                  rules={[
                                    {
                                      required: true,
                                      message: "Please Enter admin email",
                                    },
                                  ]}
                                >
                                  <Input
                                    size="large"
                                    className="rounded border border-1"
                                    placeholder="Admin E-mail"
                                  />
                                </Form.Item>
                              </Col>
                            </Row>
                          </Col>

                          <Col span={24}>
                            <Row>
                              <Col
                                xs={24} sm={24} md={4}
                                className="d-flex justify-content-start me-4 bg-white lg:mb-5"
                              >
                                <label className="font-bold">
                                  Third Party App Token <span className="text-danger">*</span>
                                </label>
                              </Col>
                              <Col xs={24} sm={24} md={12}>
                                <Form.Item
                                  name="thirdPartyAppToken"
                                  rules={[
                                    {
                                      required: true,
                                      message: "Please Enter token for third party app",
                                    },
                                  ]}
                                >
                                  <Input
                                    size="large"
                                    className="rounded border border-1"
                                    placeholder="Third Party App Token"
                                  />
                                </Form.Item>
                              </Col>
                            </Row>
                          </Col>

                          <Col span={24}>
                            <Row>
                              <Col
                                span={4}
                                className="d-flex justify-content-start me-4 bg-white mb-5"
                              >
                                <label className="font-bold">
                                  Logo <span className="text-danger">*</span>
                                </label>
                              </Col>
                              <Col span={12}>
                                <Upload
                                  name="logoimage"
                                  action="https://run.mocky.io/v3/435e224c-44fb-4773-9faf-380c5e6a2188"
                                  fileList={logo}
                                  onChange={handleLogoImage}
                                  maxCount={1}
                                  accept=".png, .jpg, .jpeg"
                                  beforeUpload={(file) => {
                                    // Validate file type and size
                                    const isJpgOrPng =
                                      file.type === "image/jpeg" ||
                                      file.type === "image/png";
                                    if (!isJpgOrPng) {
                                      message.error("You can only upload JPG/PNG file!");
                                    }
                                    const isLt2M = file.size / 1024 / 1024 < 1;
                                    if (!isLt2M) {
                                      message.error("Image must be smaller than 1MB!");
                                    }
                                    return isJpgOrPng && isLt2M;
                                  }}
                                >
                                  <Button icon={<BiUpload />}>Upload</Button>
                                </Upload>
                                {logo.length > 0 && (
                                  <div style={{ marginTop: "10px" }}>
                                    <Image
                                      src={logopreview}
                                      alt="Preview"
                                      style={{ maxWidth: "100%", height: "100px" }}
                                    />
                                  </div>
                                )}
                              </Col>
                            </Row>
                          </Col>
                          <Col span={24}>
                            <Row>
                              <Col
                                span={4}
                                className="d-flex justify-content-start me-4 bg-white mb-5"
                              >
                                <label className="font-bold">
                                  Favicon <span className="text-danger">*</span>
                                </label>
                              </Col>
                              <Col span={12}>
                                <Upload
                                  name="faviconimage"
                                  action="https://run.mocky.io/v3/435e224c-44fb-4773-9faf-380c5e6a2188"
                                  fileList={favicon}
                                  onChange={handlefaviconImage}
                                  maxCount={1}
                                  accept=".png, .jpg, .jpeg"
                                  beforeUpload={(file) => {

                                    const isJpgOrPng =
                                      file.type === "image/jpeg" ||
                                      file.type === "image/png";
                                    if (!isJpgOrPng) {
                                      message.error("You can only upload JPG/PNG file!");
                                    }
                                    const isLt2M = file.size / 1024 / 1024 < 1;
                                    if (!isLt2M) {
                                      message.error("Image must be smaller than 1MB!");
                                    }
                                    return isJpgOrPng && isLt2M;
                                  }}
                                >
                                  <div>
                                    <Button icon={<BiUpload />}>Upload</Button>
                                  </div>
                                </Upload>
                                {favicon.length > 0 && (
                                  <div style={{ marginTop: "10px" }}>
                                    <Image
                                      src={faviconpreview}
                                      alt="Preview"
                                      style={{ width: "100%", height: "100px" }}
                                    />
                                  </div>
                                )}
                              </Col>
                            </Row>
                          </Col>

                          <Col span={24} className="d-flex justify-content-center">
                            <Button className="allbuttons text-white" htmlType="submit">
                              Submit
                            </Button>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </Form></TabPane>
                <TabPane tab="Smtp" key="2">
                  <Form className="bg-white" >
                    <Row
                      className="border border-warning-2 p-5 bg-white rounded-md"
                      style={{ marginLeft: 0, marginRight: 0 }}
                    >
                      <Col xs={24} sm={24} md={24} xl={24} xxl={24}>
                        <Row className="bg-white">
                          <Col span={24}>
                            <Row>
                              <Col
                                xs={24} sm={24} md={4}
                                className="d-flex justify-content-start me-4 bg-white lg:mb-5"
                              >
                                <label className="font-bold">
                                  SMTP Host <span className="text-danger">*</span>
                                </label>
                              </Col>
                              <Col xs={24} sm={24} md={12}>
                                <Form.Item
                                  name="smtpHost"
                                  rules={[
                                    {
                                      required: true,
                                      message: "Please Enter SMTP Host",
                                    },
                                  ]}
                                >
                                  <Input
                                    size="large"
                                    className="rounded border border-1"
                                    placeholder="SMTP Host"
                                  />
                                </Form.Item>
                              </Col>
                            </Row>
                          </Col>
                          <Col span={24}>
                            <Row>
                              <Col
                                xs={24} sm={24} md={4}
                                className="d-flex justify-content-start me-4 bg-white lg:mb-5"
                              >
                                <label className="font-bold">
                                  SMTP Port <span className="text-danger">*</span>
                                </label>
                              </Col>
                              <Col xs={24} sm={24} md={12}>
                                <Form.Item
                                  name="smtpPort"
                                  rules={[
                                    {
                                      required: true,
                                      message: "Please Enter SMTP Port",
                                    },
                                  ]}
                                >
                                  <Input
                                    size="large"
                                    className="rounded border border-1"
                                    placeholder="SMTP Port"
                                  />
                                </Form.Item>
                              </Col>
                            </Row>
                          </Col>
                          <Col span={24}>
                            <Row>
                              <Col
                                xs={24} sm={24} md={4}
                                className="d-flex justify-content-start me-4 bg-white lg:mb-5"
                              >
                                <label className="font-bold">
                                  SMTP Username <span className="text-danger">*</span>
                                </label>
                              </Col>
                              <Col xs={24} sm={24} md={12}>
                                <Form.Item
                                  name="smtpUsername"
                                  rules={[
                                    {
                                      required: true,
                                      message: "Please Enter SMTP Username",
                                    },
                                  ]}
                                >
                                  <Input
                                    size="large"
                                    className="rounded border border-1"
                                    placeholder="SMTP Username"
                                  />
                                </Form.Item>
                              </Col>
                            </Row>
                          </Col>
                          <Col span={24}>
                            <Row>
                              <Col
                                xs={24} sm={24} md={4}
                                className="d-flex justify-content-start me-4 bg-white lg:mb-5"
                              >
                                <label className="font-bold">
                                  SMTP Password <span className="text-danger">*</span>
                                </label>
                              </Col>
                              <Col xs={24} sm={24} md={12}>
                                <Form.Item
                                  name="smtpPassword"
                                  rules={[
                                    {
                                      required: true,
                                      message: "Please Enter SMTP Password",
                                    },
                                  ]}
                                >
                                  <Input
                                    size="large"
                                    className="rounded border border-1"
                                    placeholder="SMTP Password"
                                  />
                                </Form.Item>
                              </Col>
                            </Row>
                          </Col>
                          <Col span={24}>
                            <Row>
                              <Col
                                xs={24} sm={24} md={4}
                                className="d-flex justify-content-start me-4 bg-white lg:mb-5"
                              >
                                <label className="font-bold">
                                  Encryption <span className="text-danger">*</span>
                                </label>
                              </Col>
                              <Col xs={24} sm={24} md={12}>
                                <Form.Item
                                  name="encryption"
                                  rules={[
                                    {
                                      required: true,
                                      message: "Please Enter Encryption",
                                    },
                                  ]}
                                >
                                  <Input
                                    size="large"
                                    className="rounded border border-1"
                                    placeholder="Encryption"
                                  />
                                </Form.Item>
                              </Col>
                            </Row>
                          </Col>
                          <Col span={24}>
                            <Row>
                              <Col
                                xs={24} sm={24} md={4}
                                className="d-flex justify-content-start me-4 bg-white lg:mb-5"
                              >
                                <label className="font-bold">
                                  From E-mail <span className="text-danger">*</span>
                                </label>
                              </Col>
                              <Col xs={24} sm={24} md={12}>
                                <Form.Item
                                  name="fromEmail"
                                  rules={[
                                    {
                                      required: true,
                                      message: "Please Enter From E-mail",
                                    },
                                  ]}
                                >
                                  <Input
                                    size="large"
                                    className="rounded border border-1"
                                    placeholder="From E-mail"
                                  />
                                </Form.Item>
                              </Col>
                            </Row>
                          </Col>
                          <Col span={24}>
                            <Row>
                              <Col
                                xs={24} sm={24} md={4}
                                className="d-flex justify-content-start me-4 bg-white lg:mb-5"
                              >
                                <label className="font-bold">
                                  From Name <span className="text-danger">*</span>
                                </label>
                              </Col>
                              <Col xs={24} sm={24} md={12}>
                                <Form.Item
                                  name="fromName"
                                  rules={[
                                    {
                                      required: true,
                                      message: "Please Enter From Name",
                                    },
                                  ]}
                                >
                                  <Input
                                    size="large"
                                    className="rounded border border-1"
                                    placeholder="From Name"
                                  />
                                </Form.Item>
                              </Col>
                            </Row>
                          </Col>
                          <Col span={24} className="d-flex justify-content-center">
                            <Button className="allbuttons text-white" htmlType="submit">
                              Submit
                            </Button>
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  </Form>
                </TabPane>
                <TabPane tab="Social Media" key="3">
                  <Form className="bg-white" >
                    <Row
                      className="border border-warning-2 p-5 bg-white rounded-md"
                      style={{ marginLeft: 0, marginRight: 0 }}
                    >
                      <Col span={24}>
                        <Row>
                          <Col
                            xs={24} sm={24} md={4}
                            className="d-flex justify-content-start me-4 bg-white lg:mb-5"
                          >
                            <label className="font-bold">
                              Contact E-mail <span className="text-danger">*</span>
                            </label>
                          </Col>
                          <Col xs={24} sm={24} md={12}>
                            <Form.Item
                              name="contactEmail"
                              rules={[
                                {
                                  required: true,
                                  message: "Please Enter Contact Email",
                                },
                              ]}
                            >
                              <Input
                                size="large"
                                className="rounded border border-1"
                                placeholder="Contact E-mail"
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Col>
                      <Col span={24}>
                        <Row>
                          <Col
                            xs={24} sm={24} md={4}
                            className="d-flex justify-content-start me-4 bg-white lg:mb-5"
                          >
                            <label className="font-bold">
                              Facebook <span className="text-danger">*</span>
                            </label>
                          </Col>
                          <Col xs={24} sm={24} md={12}>
                            <Form.Item
                              name="facebookLink"
                              rules={[
                                {
                                  required: true,
                                  message: "Please Enter FaceBook Link",
                                },
                              ]}
                            >
                              <Input
                                size="large"
                                className="rounded border border-1"
                                placeholder="Facebook Link"
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Col>

                      <Col span={24}>
                        <Row>
                          <Col
                            xs={24} sm={24} md={4}
                            className="d-flex justify-content-start me-4 bg-white lg:mb-5"
                          >
                            <label className="font-bold">
                              LinkedIn <span className="text-danger">*</span>
                            </label>
                          </Col>
                          <Col xs={24} sm={24} md={12}>
                            <Form.Item
                              name="linkedinLink"
                              rules={[
                                {
                                  required: true,
                                  message: "Please Enter linkedin Link",
                                },
                              ]}
                            >
                              <Input
                                size="large"
                                className="rounded border border-1"
                                placeholder="LinkedIn Link"
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Col>

                      <Col span={24}>
                        <Row>
                          <Col
                            xs={24} sm={24} md={4}
                            className="d-flex justify-content-start me-4 bg-white lg:mb-5"
                          >
                            <label className="font-bold">
                              Instagram <span className="text-danger">*</span>
                            </label>
                          </Col>
                          <Col xs={24} sm={24} md={12}>
                            <Form.Item
                              name="instagramLink"
                              rules={[
                                {
                                  required: true,
                                  message: "Please Enter Instagram Link",
                                },
                              ]}
                            >
                              <Input
                                size="large"
                                className="rounded border border-1"
                                placeholder="Instagram Link"
                              />
                            </Form.Item>
                          </Col>
                        </Row>
                      </Col>
                      

                      {/* <Col span={24}>
                            <Row>
                              <Col
                                xs={24} sm={24} md={4}
                                className="d-flex justify-content-start me-4 bg-white lg:mb-5"
                              >
                                <label className="font-bold">
                                  Twitter <span className="text-danger">*</span>
                                </label>
                              </Col>
                              <Col xs={24} sm={24} md={12}>
                                <Form.Item
                                  name="twitterLink"
                                  rules={[
                                    {
                                      required: true,
                                      message: "Please Enter Twitter Link",
                                    },
                                  ]}
                                >
                                  <Input
                                    size="large"
                                    className="rounded border border-1"
                                    placeholder="Twitter Link"
                                  />
                                </Form.Item>
                              </Col>
                            </Row>
                          </Col> */}

                          <Col span={24}>
                            <Row>
                              <Col
                                xs={24} sm={24} md={4}
                                className="d-flex justify-content-start me-4 bg-white lg:mb-5"
                              >
                                <label className="font-bold">
                                  Third Party App Token <span className="text-danger">*</span>
                                </label>
                              </Col>
                              {/* ... existing thirdPartyAppToken field ... */}
                            </Row>
                          </Col>
                      <Col span={24} className="d-flex justify-content-center">
                        <Button className="allbuttons text-white" htmlType="submit">
                          Submit
                        </Button>
                      </Col>
                    </Row>
                  </Form>
                </TabPane>

                <TabPane tab="AI" key="4">
                  <div className="p-2 bg-white rounded-md border border-warning-2 p-5" style={{ marginLeft: 0, marginRight: 0 }}>
                    {/* Header: Search */}
                    <Row className="mb-4" align="middle" justify="end" gutter={[16, 16]}>
                      <Col xs={24} md={12}>
                        <div className="flex flex-col sm:flex-row gap-2 w-full">
                          <Input
                            prefix={<SearchOutlined />}
                            size="large"
                            placeholder="Search AI settings..."
                            allowClear
                            value={aiSearchInput}
                            onChange={(e) => setAiSearchInput(e.target.value)}
                            className="flex-1"
                            style={{ width: "100%", height: "33px" }}
                          />
                        </div>
                      </Col>
                    </Row>

                    {/* Table */}
                    <Row justify="center">
                      <Col span={24}>
                        <Spin spinning={aiLoading}>
                          <div className="scrollbar-hide custom-scroll-table" style={{ overflowX: "auto" }}>
                            {aiSettings.length === 0 && !aiLoading ? (
                              <Empty
                                description="No AI configurations found"
                                style={{ padding: "60px 0" }}
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                              />
                            ) : (
                              <Table
                                columns={aiColumns}
                                dataSource={aiSettings}
                                rowKey="_id"
                                scroll={{ x: "max-content" }}
                                pagination={{
                                  pageSize: PAGE_SIZE,
                                  current: aiPage,
                                  total: aiTotal,
                                  showSizeChanger: false,
                                  onChange: (newPage) => setAiPage(newPage),
                                  showTotal: (t) => `Total ${t} configurations`,
                                }}
                              />
                            )}
                          </div>
                        </Spin>
                      </Col>
                    </Row>
                    {/* ── Global System Prompt ── */}
                    <div
                      style={{
                        marginTop: 28,
                        background: "#f8faff",
                        border: "1px solid #e6eaf3",
                        borderRadius: 12,
                        padding: "24px 28px",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                        <span style={{ fontSize: 18 }}>🧠</span>
                        <span style={{ fontWeight: 700, fontSize: 16, color: "#1e293b" }}>
                          Global System Prompt
                        </span>
                        <Tooltip title="This prompt is applied to ALL AI model configurations. Use it to define the assistant's behaviour, tone, and ERP context.">
                          <InfoCircleOutlined style={{ color: "#8c8c8c", fontSize: 14 }} />
                        </Tooltip>
                      </div>
                      <p style={{ color: "#64748b", fontSize: 13, marginBottom: 14, lineHeight: 1.6 }}>
                        This prompt applies to <strong>every AI model</strong> in the table above. Define the assistant's role, tone, and ERP knowledge here — no need to repeat it per model.
                      </p>
                      <Input.TextArea
                        rows={10}
                        value={globalPrompt}
                        onChange={(e) => setGlobalPrompt(e.target.value)}
                        placeholder={`You are an intelligent ERP Assistant for Encrypted Infoweb. You help users analyze business data, answer questions, and provide actionable summaries across all ERP modules.\n\n## Your Capabilities\nYou can assist with:\n- Customers & CRM – customer lists, leads, follow-ups, deal status\n- Projects & Tasks – project timelines, task assignments, pending/completed status\n- HRMS – employee records, attendance, leave requests, payroll summaries\n- Accounting – invoices, payments, expenses, financial summaries\n\n## Response Guidelines\n- Always respond in clear, concise English\n- Use tables for listing multiple records\n- Use bullet points for summaries\n- Highlight important numbers or statuses in bold`}
                        style={{
                          fontFamily: "'Fira Code', 'Courier New', monospace",
                          fontSize: 13,
                          borderRadius: 8,
                          border: "1px solid #cbd5e1",
                          background: "#fff",
                          resize: "vertical",
                        }}
                      />
                      <div style={{ textAlign: "right", marginTop: 14 }}>
                        <Button
                          type="primary"
                          loading={globalPromptSaving}
                          onClick={handleSaveGlobalPrompt}
                          style={{ background: "#001529", border: "none", borderRadius: 8, height: 38, paddingInline: 24 }}
                        >
                          Save System Prompt
                        </Button>
                      </div>
                    </div>
                  </div>
                </TabPane>

              </Tabs>
              {/* <Col span={18}>
                <h2 className="text-2xl font-bold">Settings</h2>
              </Col> */}
            </div>
          </Card>
        </Col >

      </Row >
      {/* Add / Edit AI Modal */}
      <Modal
        style={{ bottom: 20, top: 20, margin: "0 auto", position: "relative" }}
        styles={{
          body:    { overflowY: "auto", fontFamily: "inter, sans-serif" },
          header:  { padding: "12px", position: "sticky", top: 0, backgroundColor: "#f1f5ffff" },
          content: { padding: 0, overflow: "hidden" },
        }}
        title={
          <div style={{ color: "#4b5563", fontFamily: "inter, sans-serif", fontSize: "20px", fontWeight: 500, display: "flex", alignItems: "center", gap: "8px" }}>
            {editingAi ? "Edit AI Configuration" : "Add AI Configuration"}
            <Tooltip title="Configure credentials for AI models used in Chatbot">
              <InfoCircleOutlined style={{ color: "#8c8c8c" }} />
            </Tooltip>
          </div>
        }
        open={aiModalVisible}
        onCancel={() => setAiModalVisible(false)}
        footer={null}
        width={650}
      >
        <div style={{ padding: "24px" }}>
          <Form form={aiForm} layout="vertical" requiredMark={false}>
            <Row gutter={[16, 0]}>
              <Col span={24}>
                <Form.Item label="Configuration Title" name="title" rules={[{ required: true, message: "Please enter configuration title" }]}>
                  <Input size="large" className="rounded border border-1" placeholder="e.g. Gemini Primary, OpenRouter Backup" />
                </Form.Item>
              </Col>
              
              <Col xs={24} sm={12}>
                <Form.Item label="API Key" name="apiKey" rules={[{ required: true, message: "Please enter API Key" }]}>
                  <Input.Password size="large" placeholder="Enter API Key" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item label="Model" name="model" rules={[{ required: true, message: "Please select or enter Model" }]}>
                  <Select
                    placeholder="Select or type custom Model"
                    options={computedAiOptions}
                    showSearch
                    size="large"
                    optionFilterProp="label"
                    onSearch={setAiModelSearchValue}
                    onChange={(val) => {
                      aiForm.setFieldsValue({ model: val });
                      setAiModelSearchValue("");
                    }}
                    filterOption={(input, option) =>
                      (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item label="Provider" name="provider" rules={[{ required: true, message: "Please enter Provider" }]}>
                  <Input size="large" className="rounded border border-1" placeholder="e.g. google, openai, openrouter" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item label="API Base Endpoint" name="apiBase">
                  <Input size="large" className="rounded border border-1" placeholder="e.g. https://api.openai.com/v1" />
                </Form.Item>
              </Col>

              {/* System Prompt moved to global section below the table */}
            </Row>
          </Form>
        </div>

        <div style={{ padding: "16px 24px", borderTop: "1px solid #f0f0f0" }}>
          <div style={{ textAlign: "right" }}>
            <Space>
              <Button onClick={() => setAiModalVisible(false)}>Cancel</Button>
              <Button
                type="primary"
                onClick={handleAiSubmit}
                loading={aiLoading}
                style={{ background: "#001529", color: "#ffffff", border: "none" }}
              >
                {editingAi ? "Update" : "Save"}
              </Button>
            </Space>
          </div>
        </div>
      </Modal>

      {/* Delete AI Confirmation Modal */}
      <Modal
        open={!!aiDeleteId}
        onOk={handleDeleteAi}
        onCancel={() => setAiDeleteId(null)}
        okText="Yes"
        cancelText="No"
        width={500}
        centered={false}
        destroyOnClose
        className="custom-modal"
        style={{ bottom: 20, top: "10vw", maxHeight: "calc(100vh - 30px)", margin: "0 auto", position: "relative" }}
        title={<div style={{ marginLeft: "11px", width: "100%", paddingRight: 20 }}>Confirmation</div>}
        okButtonProps={{ style: { backgroundColor: "#ff4d4f", borderColor: "#ff4d4f", color: "white" } }}
        bodyStyle={{ padding: "22px", overflowY: "auto", fontFamily: "inter, sans-serif" }}
        styles={{
          header: { borderBottom: "1px solid #e0e0e0", padding: "12px", position: "sticky", top: 0, backgroundColor: "white" },
          content: { padding: 0, overflow: "hidden" },
          footer: { borderTop: "1px solid #e0e0e0", padding: "12px", position: "sticky", top: 0, backgroundColor: "white" },
        }}
      >
        <p>Are you sure you want to delete this AI Configuration?</p>
      </Modal>
    </div >
  )
}


export default Setting;
